import { Injectable } from '@angular/core';
import { SearchParams } from 'src/app/angular-elements/producer/interfaces/search-params.interface';
import { SearchResponse } from 'src/app/angular-elements/producer/interfaces/search-response.interface';
import {
  RequestConfigImplementation,
  REQUEST_METHODS,
  XHR_STRATEGIES,
  HTTPWrapperContext
} from '@ptl/xhr';
import { HttpErrorResponse, HttpHeaders } from '@angular/common/http';
import { RetrieveApp } from '../interfaces/application-retrieval/retrieveApp.interface';
import { StateStructure } from '@ptl/state';
import { NgRedux } from '@ptl/angular-redux-store';
import { setActiveProductGroup, savePlanIdForEnrollment, saveMedigapValuesPostFormSubmit, saveSinglePlan, selectPlanForDRX, setPlanChange } from 'src/app/angular-elements/plan-selection/configs/reducers/plans.reducer';
import { PRODUCT_GROUPS, PRODUCT_TYPES } from 'src/app/angular-elements/plan-selection/enums/product-groups.enum';
import { ProductTypeTabService } from 'src/app/angular-elements/plan-selection/services/product-type-tab.service';
import { RetrievePlansService } from 'src/app/angular-elements/plan-selection/plan-summary-list/retrieve-plans.service';
import { GetProductTypesResponse } from 'src/app/angular-elements/plan-selection/interfaces/get-product-types.interfaces';
//import * as moment from 'moment';
import moment from 'moment';
import { setEffectiveDate } from 'src/app/angular-elements/shared/components/configs/reducers/effective-date.reducer';
import { DateService } from './date.service';
import { MedicareAdvantage_and_PDP_Product, Product } from '../../angular-elements/plan-selection/interfaces/product.interface';
import { loadExistingHealthScreeningIntoState } from '../reducers/health-screening.reducer';
import { loadExistingMedicationsIntoState } from '../reducers/medigap-medications.reducer';
import { loadExistingApplicantInfoIntoState } from '../reducers/applicant-info.reducer';
import { loadExistingAgencyIntoState } from '../../angular-elements/producer/configs/reducers/producer.reducer';
import { setDrxSingleProductResponse } from '../../angular-elements/medical-information/medication-information/reducers/drx-response.reducer';
import { DrxService } from '../../angular-elements/medical-information/medication-information/services/drx.service';
import { DRXRetrieveSingleProductResponse, PharmacyCost } from '../interfaces/drx.interface';
import { setPharmacyState } from '../../angular-elements/medical-information/pharmacy-information/configs/reducers/pharmacy.reducer';
import { IndividualMarketStateStructure } from '../../angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/interfaces/individualMarket.interfaceStateStructure';
import { APPLICANT_RELATION, SPOUSE_RELATIONSHIPS } from '../../angular-elements/plan-selection/services/interfaces/shared/plan-demographics/applicant.interface';
import { saveIndividualState } from '../../angular-elements/form-wrappers/plan-enrollment/individual-plan-enrollment-form/configs/reducers/individual-market.reducer';
import { PharmacyInformationService } from '../../angular-elements/medical-information/pharmacy-information/services/pharmacy-information.service';
import { RawPharmacy } from '../../angular-elements/medical-information/pharmacy-information/configs/interfaces/pharmacy-lookup/response/response.interface';
import { Pharmacy } from '../../angular-elements/medical-information/pharmacy-information/configs/interfaces/pharmacy.interface';
import { setSelectedElectionPeriodEvent } from '../reducers/election-period.reducer';
import { MultiCheckboxMap } from '../../angular-elements/shared/interfaces/personalInfoStateStructure.interface';
import { savePrepopulatedData } from '../../angular-elements/shared/components/configs/reducers/prePopulatedData.reducer';
import { PrepopulatedFields } from '../../angular-elements/shared/components/interfaces/prepopulated-fields.interface';
import { environment } from 'src/environments/environment';
import { EnvironmentConfig } from 'src/environments/environment.interface';
import { CommonUtilityService } from '../utility-services/common-utility.service';
import { storeApplicationTrackingId, saveElectionAppIn } from '../reducers/enrollmentApp.reducer';
import { loadEnrollmentInfo } from '../reducers/enrollment.reducer';
import { loadAuthorizedRepresentative } from '../reducers/authorized-representative.reducer';
import { loadPreliminaryInfo } from '../reducers/preliminary.reducer';
import { loadPersonalInfo } from '../reducers/personalInfo.reducer';
import { loadInsuranceInfo } from '../reducers/insuranceInfo.reducer';
import { loadPayment } from '../reducers/payment.reducer';
import { loadStatementAuth } from '../reducers/statementAuth.reducer';
import { loadMedigapEligibilityInfo } from '../reducers/medigapEligibilityCheck.reducer';
import { PlanAttributes, SummaryView } from 'src/app/angular-elements/plan-selection/interfaces/plan-attributes.interface';
import { API_ENDPOINTS } from '../constants/api-endpoints.constant';

const _environment: EnvironmentConfig = environment;

@Injectable({
  providedIn: 'root'
})
export class ApplicationStatusRetrievalService {
  
  constructor(
    private httpClient: HTTPWrapperContext,
    private ngRedux: NgRedux<StateStructure>,
    private commonUtilityService: CommonUtilityService,
    private productTypeTabService: ProductTypeTabService,
    private retrievePlansService: RetrievePlansService,
    private dateUtility: DateService,
    private drxService: DrxService,
    private pharmService: PharmacyInformationService
  ) {

  }

  getApplications(searchParams: SearchParams): Promise<SearchResponse> {
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: '/sxesvc/api/v3/retailapp/retailAppSearch',
      headers: new HttpHeaders({
        // Use the global ASP ddtoken to pool together all ASP sub-brands
        ddToken: btoa(_environment.initData.brand.ddToken),
      }),
      POST_OR_PUT_PAYLOAD: searchParams
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);
    return this.httpClient.makeRequest(requestConfig);
  }

  async getApplication(appId: string, code?: string): Promise<RetrieveApp | HttpErrorResponse> {
    const payload = {
      trackingId: appId
    };
    if (code) {
      payload['agencyCode'] = code;
    }
    const requestConfig = new RequestConfigImplementation({
      method: REQUEST_METHODS.POST,
      url: API_ENDPOINTS.RETAIL_APP.INFO,
      headers: new HttpHeaders({
      }),
      POST_OR_PUT_PAYLOAD: payload
    });
    this.httpClient.setStrategy(XHR_STRATEGIES.AXWAY_POST_PASS);

    const response: RetrieveApp | HttpErrorResponse = await this.httpClient.makeRequest<RetrieveApp>(requestConfig)
    .catch( (error: HttpErrorResponse) => {
      return error;
    });

    return response;
  }


  /** Returns TRUE if values saved successfully, FALSE otherwise */
  async applyRetrievedDataToState(appId: string, code?: string): Promise<boolean> {
    try {
      const retrieveAppResponse: RetrieveApp | HttpErrorResponse = await this.getApplication(appId, code);
      if(retrieveAppResponse instanceof HttpErrorResponse){
        console.error(
          `No Application Found!
          AppId: ${appId}
          AgencyCode (Optional): ${code}`
        );
        return false;
      }

      const retrieveApp = retrieveAppResponse as RetrieveApp;
      let appData = (retrieveApp as RetrieveApp).enrollementObject;
      let productGroup = retrieveApp && appData.productDescription;
      let planDetails = null;

      if (appData.enrollment) {
        const enrollmentData = appData.enrollment;

        if (enrollmentData.effectiveDate) {
          this.ngRedux.dispatch(setEffectiveDate(moment(enrollmentData.effectiveDate).format('YYYY-MM-DD').toString()));
        }

        if (retrieveApp.applicationTrackingId) {
          this.ngRedux.dispatch(storeApplicationTrackingId(retrieveApp.applicationTrackingId));
        }

        if (appData.customerId || appData.opportunityId) {
          this.ngRedux.dispatch(savePrepopulatedData({customerId: appData.customerId, opportunityId: appData.opportunityId} as PrepopulatedFields));
        }

        // Need to get enrollmentData.productData.planDetails -> componentStates.Plans.plansByPlanType [product group] [plan Id]
        if (productGroup && enrollmentData.productData) {
          if (enrollmentData.productData.planDetails?.attributes) {
            switch (productGroup) {
              case PRODUCT_GROUPS.MEDICARE_SUPPLEMENT:
              case PRODUCT_GROUPS.MEDIGAP:
                planDetails = appData.enrollment.productData.planDetails; // as unknown as PlanCollection<MedigapProduct>; // Product<PlanAttributes, SummaryView>
                await this.storeMedicarePlansState(appData, planDetails, productGroup, retrieveApp);
                break;
              case PRODUCT_GROUPS.MEDICARE_ADVANTAGE:
              case PRODUCT_GROUPS.PRESCRIPTION_DRUG_PLAN:
              case PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE:
                planDetails = appData.enrollment.productData.planDetails; // as unknown as PlanCollection<MedicareAdvantage_and_PDP_Product>;
                await this.storeMedicarePlansState(appData, planDetails, productGroup, retrieveApp);
                break;
              case PRODUCT_GROUPS.OFF_EXCHANGE:
              case PRODUCT_GROUPS.DENTAL:
              case PRODUCT_GROUPS.BLUE_EDGE_BALANCE:
                planDetails = appData.enrollment.productData.planDetails; // as unknown as PlanCollection<IndividualMarketProduct>;
                await this.storeIndividualPlansState(appData, planDetails, productGroup, retrieveApp);
                break;
              default:
                console.error(`Unknown application product group: ${productGroup}`);
                return false;
            }
          }
          else {
            // TODO - WY Medicare Supplement doesn't have attributes sometimes?
            planDetails = appData.enrollment.productData.planDetails; // as unknown as PlanCollection<MedigapProduct>;
            await this.storeMedicarePlansState(appData, planDetails, productGroup, retrieveApp);
          }
        }
      }

      return true;

    } catch (e) {
      console.error(e);
      return false;
    }
  }

  async storeMedicarePlansState(appData, planDetails, productGroup, retrieveApp) {

    if (!appData.personalInfo) {
      return false;
    }

    await this.commonUtilityService.setBrandByZipCode(appData.personalInfo.zipCode);

    // For Medigap, if the app is in progress we will push the coverage date to next month if it's in the past
    // Note: This code is for user-friendliness. It's also handled when the app is submitted, but MG only updates this value in the Pre-Auth form
    if (appData.enrollment.coverageDate) {
      if (appData.submitStatus === 'IN_PROGRESS') {
        appData.enrollment.coverageDate = this.dateUtility.getValidCoverageDate(appData.enrollment.coverageDate);
      }
      else {
        appData.enrollment.coverageDate = appData.enrollment.coverageDate;
      }
    }

    this.ngRedux.dispatch(loadEnrollmentInfo(appData.enrollment));
    this.ngRedux.dispatch(saveElectionAppIn(appData.enrollment.saveElectionAppIn));
    this.ngRedux.dispatch(setPlanChange(appData.productType && appData.productType.indexOf(PRODUCT_TYPES.PLAN_CHANGE_SUFFX) !== -1));

    this.ngRedux.dispatch(saveMedigapValuesPostFormSubmit({
      gender: appData.personalInfo?.gender,
      macra: appData.enrollment?.macra,
      tobaccoUsage: appData.enrollment?.tobaccoUsage,
      dob: this.dateUtility.getDateAsFormattedString(appData.personalInfo?.dob, 'YYYY-MM-DD'),
      coverageDate: this.dateUtility.getDateAsFormattedString(appData.enrollment?.coverageDate, 'YYYY-MM-DD'),
      // We were not saving these values prior to Dec 2021
      whb: appData.enrollment?.whb,
      hardStops: appData.enrollment?.hardStops || 'false'
    }));

    if (appData.enrollment.productData.drxSingleProductResponse) {
      //medicareAdvantage_or_PDP_planForDRX
      await this.applyPharmaciesToState(appData.enrollment.productData.drxSingleProductResponse)
      this.ngRedux.dispatch(setDrxSingleProductResponse(appData.enrollment.productData.drxSingleProductResponse));
      this.drxService.generateAndSaveRetailAndMailData();
    }
    
    // save the election input response to the component state
    if (appData.ElectionPeriod?.selectedElectionPeriodEvent) {
      this.ngRedux.dispatch(setSelectedElectionPeriodEvent(appData.ElectionPeriod.selectedElectionPeriodEvent));
    }

    this.ngRedux.dispatch(loadExistingMedicationsIntoState(appData.isPrescribedMeds, appData.medications ? appData.medications : []));
    this.ngRedux.dispatch(loadExistingAgencyIntoState(appData.agentInfo));
    this.ngRedux.dispatch(loadExistingHealthScreeningIntoState(appData.healthScreening));
    this.ngRedux.dispatch(loadExistingApplicantInfoIntoState(appData.applicantInfo));

    if (planDetails) {
      this.ngRedux.dispatch(saveSinglePlan(productGroup, planDetails));
      this.ngRedux.dispatch(selectPlanForDRX(planDetails));
    } else if (appData.enrollment.productData?.planDetails?.year) {
      // If we don't have plan details, we can try looking up all the info instead
      const productTypesResponse: GetProductTypesResponse = await this.productTypeTabService.retrieveProductTypes();

      const planYear = appData.enrollment.productData['planDetails']['year'].toString();

      await this.retrievePlansService.getPlans(productTypesResponse.productGroups, planYear);
    }

    /**
     * PRODUCER FLOW - possible that the user lacks a selected plan and this is more of a
     *  pre-population of data flow (user sent to View Plans)
     * CONSUMER FLOW - user should be accessing an existing enrollment where the plan was
     *  selected
     */
    if (
      appData.productDescription &&
      appData.enrollment.productData.planId
    ) {
      this.ngRedux.dispatch(setActiveProductGroup(appData.productDescription as PRODUCT_GROUPS));
      this.ngRedux.dispatch(savePlanIdForEnrollment(appData.enrollment.productData.planId));
    }
    if (appData.enrollment.productData.planName === PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE) {
      this.ngRedux.dispatch(setActiveProductGroup(PRODUCT_GROUPS.WHOLE_HEALTH_BALANCE));
    }
    if (appData.authRep) {
      this.ngRedux.dispatch(loadAuthorizedRepresentative(appData.authRep));
    }
    if (appData.preliminary) {
      this.ngRedux.dispatch(loadPreliminaryInfo(appData.preliminary));
    }
    if (appData.personalInfo) {
      // convert multi-checkbox codes to state mapped fields
      appData.personalInfo.ethnicityCodes = this.convertArrayToMultiCheckboxState(appData.enrollment.ethnicityCodes);
      appData.personalInfo.raceCodes = this.convertArrayToMultiCheckboxState(appData.enrollment.raceCodes);
      this.ngRedux.dispatch(loadPersonalInfo(appData.personalInfo));
    }
    if (appData.insuranceInfo) {
      this.ngRedux.dispatch(loadInsuranceInfo(appData.insuranceInfo));
    }
    if (appData.payment) {
      this.ngRedux.dispatch(loadPayment(appData.payment));
    }
    if (appData.statementAuth) {
      this.ngRedux.dispatch(loadStatementAuth(appData.statementAuth));
    }
    if (appData.medigapEligibilityCheck) {
      this.ngRedux.dispatch(loadMedigapEligibilityInfo(appData.medigapEligibilityCheck));
    }

    return true;
  }

  async storeIndividualPlansState(appData, planDetails: Product<PlanAttributes, SummaryView>, productGroup, retrieveApp) {
    const indState: IndividualMarketStateStructure = {} as IndividualMarketStateStructure;

    const subscriber = appData.applicant.filter((person) => person.relationship === APPLICANT_RELATION.SUBSCRIBER)[0];
    const subscriberFormatted = { 0: subscriber };
    let spouse = appData.applicant.filter((person) => SPOUSE_RELATIONSHIPS.indexOf(person.relationship) !== -1)[0];
    const spouseFormatted = { 0: spouse };
    const dependents = appData.applicant.filter((person) => person.relationship === APPLICANT_RELATION.DEPENDENT);
    const dependentsFormatted = (() => {
      const returnable = {};
      dependents.forEach( (d, i) => {
        returnable[i] = d;
      });
      return returnable;
    })();

    await this.commonUtilityService.setBrandByZipCode(subscriber.zipCode);

    indState.ratingInfo = {
      subscriber: subscriberFormatted,
      'spousal-partner': spouseFormatted,
      dependents: dependentsFormatted,
      effectiveDate: appData.enrollment.effectiveDate ? moment(appData.enrollment.effectiveDate).format('YYYY-MM-DD').toString() : ''
    };

    indState.enrollment = appData.enrollment;

    indState.childOnlyPolicy = indState.enrollment.childOnlyPolicy;

    if (planDetails) {
      this.ngRedux.dispatch(saveSinglePlan(productGroup, planDetails));
      this.ngRedux.dispatch(selectPlanForDRX(planDetails as MedicareAdvantage_and_PDP_Product)); // I feel like the MedicareAdvantage_and_PDP_Product is overly specific now, but, no issues
      this.ngRedux.dispatch(setActiveProductGroup('Off-Exchange'));
    }
    else {
      console.warn('no product in app payload')
    }

    this.ngRedux.dispatch(saveIndividualState(indState));
  }

  private async applyPharmaciesToState(drxData: DRXRetrieveSingleProductResponse) {
    let pharmCosts = drxData.PharmacyCosts;
    let convertedPharmacies: Pharmacy[] = [];
    let pharmPromises = [];
    let pharmacyIds = pharmCosts.map((pharmCost: PharmacyCost) => pharmCost.PharmacyID);

    pharmacyIds.forEach(id => id ? pharmPromises.push(this.pharmService.getPharmacyById(id)) : null);

    await Promise.all(pharmPromises).then((pharmResponses: RawPharmacy[]) => {
      pharmResponses.forEach((rawPharm) => {
        convertedPharmacies.push({
          name: rawPharm.Name,
          address1: rawPharm.Address1,
          address2: rawPharm.Address2,
          city: rawPharm.City,
          state: rawPharm.State,
          pharmacyId: rawPharm.PharmacyID,
          pharmacyNpi: rawPharm.PharmacyNPI,
          isSelected: true
        });
      });

      // default to first pharmacy as the selected one
      this.ngRedux.dispatch(setPharmacyState(convertedPharmacies, convertedPharmacies[0]));
    });
  }

  // Converts an array of codes ['code1', 'code2', 'code3'] to an object meant for checkboxes { code1: true, code2: true, code3: true }
  // Values in the array are assumed to be 'checked' codes
  private convertArrayToMultiCheckboxState(arr: string[]): MultiCheckboxMap {
    if (!arr?.length) {
      return {};
    }

    return arr.reduce(function(map, obj) {
      map[obj] = true;
      return map;
    }, {});
  }
}
